<template>
  <form @submit.prevent="submit">
    <div class="field">
      <div class="label">Localization Name</div>
      <div>
        <VInput
          v-bind="nameAttrs"
          class="w-80"
          type="text"
          :maxlength="255"
          :error="!!errors.name"
          placeholder="[Client] - [Asset] ([EPSG])"
          @input="setValues({ name: $event.target.value }), $emit('input', values)"
        />
        <div v-if="isNameAlreadyUsed" class="error">This name is already being used.</div>
        <div v-if="errors.name" class="error">{{ errors.name }}</div>
      </div>
    </div>

    <div class="field">
      <div class="label">Easting (X axis)</div>
      <div>
        <VInput
          v-bind="eastingAttrs"
          class="w-80"
          type="number"
          :error="!!errors.easting"
          placeholder="Enter X axis here"
          @input="setValues({ easting: +$event.target.value }), $emit('input', values)"
        />
        <div v-if="errors.easting" class="error">{{ errors.easting }}</div>
      </div>
    </div>

    <div class="field">
      <div class="label">Northing (Y axis)</div>
      <div>
        <VInput
          v-bind="northingAttrs"
          class="w-80"
          type="number"
          :error="!!errors.northing"
          placeholder="Enter Y axis here"
          @input="setValues({ northing: +$event.target.value }), $emit('input', values)"
        />
        <div v-if="errors.northing" class="error">{{ errors.northing }}</div>
      </div>
    </div>

    <div class="field">
      <div class="label">Elevation (Z axis)</div>
      <div>
        <VInput
          v-bind="elevationAttrs"
          class="w-80"
          type="number"
          :error="!!errors.elevation"
          placeholder="Enter Z axis here"
          @input="setValues({ elevation: +$event.target.value }), $emit('input', values)"
        />
        <div v-if="errors.elevation" class="error">{{ errors.elevation }}</div>
      </div>
    </div>

    <div class="field">
      <div class="label">Rotation (degrees)</div>
      <div>
        <VInput
          v-bind="rotationAttrs"
          class="w-80"
          type="number"
          :error="!!errors.rotation"
          placeholder="Enter azimuth rotation in degrees"
          @input="setValues({ rotation: +$event.target.value }), $emit('input', values)"
        />
        <div v-if="errors.rotation" class="error">{{ errors.rotation }}</div>
      </div>
    </div>

    <div class="field">
      <div class="label">Unit of measurement</div>
      <div class="w-80">
        <VDropdown v-model="currentMeasurement" :options="measurementOptions" container-classes="w-full" size="sm">
          <template #current-single="{ item }">
            <div>{{ item.name }}</div>
          </template>

          <template #option="{ item }">
            <VDropdownItem :item="item.name" />
          </template>
        </VDropdown>
      </div>
    </div>
    <div class="flex items-center justify-end space-x-4 pt-6 border-t border-gray-100">
      <VButton variant="light" @click="$emit('cancel')">Cancel</VButton>
      <VButton type="submit" variant="copper">{{ submitBtnText }}</VButton>
    </div>
  </form>
</template>

<script lang="ts" setup>
import { useForm } from 'vee-validate'
import { toTypedSchema } from '@vee-validate/zod'
import { z } from 'zod'

import type { Option } from '@/components/VDropdown.vue'

const props = defineProps({
  isNameAlreadyUsed: {
    type: Boolean,
    default: false,
  },
  submitBtnText: {
    type: String,
    default: 'Save',
  },
})

const emit = defineEmits(['input', 'error', 'cancel', 'submit'])

const measurementOptions: Option[] = [
  { id: 'm', name: 'metres' },
  { id: 'f', name: 'feet' },
]

const validationSchema = z.object({
  name: z.string().min(1).max(255).default(''),
  elevation: z.number().min(-99999999).max(99999999),
  northing: z.number().min(-99999999).max(99999999),
  easting: z.number().min(-99999999).max(99999999),
  rotation: z.number().min(-360).max(360),
  unit_of_measurement: z.string().default('m'),
})

type DrillDataForm = z.infer<typeof validationSchema>
const { errors, defineField, setValues, values, handleSubmit } = useForm<DrillDataForm>({
  validationSchema: toTypedSchema(validationSchema),
})

watch(errors, () => {
  emit('error', errors.value)
})

const [name, nameAttrs] = defineField('name')
const [easting, eastingAttrs] = defineField('easting')
const [northing, northingAttrs] = defineField('northing')
const [elevation, elevationAttrs] = defineField('elevation')
const [rotation, rotationAttrs] = defineField('rotation')
const [unit_of_measurement, unit_of_measurementAttrs] = defineField('unit_of_measurement')

const submit = handleSubmit(() => {
  emit('submit')
})

const currentMeasurement = ref<any>(measurementOptions[0])
watch(currentMeasurement, () => {
  setValues({ unit_of_measurement: currentMeasurement.value.id })
  emit('input', values)
})
</script>

<style lang="stylus" scoped>
.field
  @apply flex flex-row mb-6
  .label
    @apply w-48 leading-8
  .error
    @apply text-sm
</style>
